import { CheckCircle2, ForwardChevrons, Keyboard, X } from '@griply/assets';
import { Task, TaskType, Timestamp } from '@griply/types';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { PopupMenuList } from 'shared/components/ui/popup-menu';
import { QuickActionRow } from 'shared/components/ui/quick-action-row';
import { Tooltip } from 'shared/components/ui/tooltip';

import { OverdueEntryPrompt } from './types';
import { useCompleteListActions } from './use-complete-list-actions';

export type CompleteListProps = {
  task: Task;
  onClose: () => void;
  date?: Timestamp;
  onOverdue: (prompt: OverdueEntryPrompt) => void;
};

export const CompleteList: React.FC<CompleteListProps> = ({
  task,
  onClose,
  date,
  onOverdue,
}) => {
  const { t } = useTranslation();
  const {
    completed,
    skippedTodayOrAfter,
    failedToday,
    onComplete,
    onSkip,
    onFail,
    onLog,
  } = useCompleteListActions(task, { date, onOverdue, onClose });

  const isHabit = [TaskType.Habit, TaskType.Repeating].includes(task.type);

  const completeRef = useRef<HTMLButtonElement>(null);
  const skipRef = useRef<HTMLButtonElement>(null);
  const failRef = useRef<HTMLButtonElement>(null);
  const logRef = useRef<HTMLButtonElement>(null);

  const completeLabel = t(
    task.completedAt
      ? 'context-menus.task.labels.un-complete'
      : 'context-menus.task.labels.complete',
  );

  return (
    <PopupMenuList hasBottomBorder>
      <QuickActionRow label={t('context-menus.task.labels.complete')}>
        <IconButton
          ref={completeRef}
          icon={CheckCircle2}
          onClick={onComplete}
          color={Colors.Success}
          highlighted={completed}
          aria-label={completeLabel}
        />
        <Tooltip referenceElement={completeRef} label={completeLabel} />
        {isHabit && !skippedTodayOrAfter && (
          <>
            <IconButton
              ref={skipRef}
              icon={ForwardChevrons}
              onClick={onSkip}
              color={Colors.Warning}
              aria-label={t('context-menus.task.labels.skip')}
            />
            <Tooltip
              referenceElement={skipRef}
              label={t('context-menus.task.labels.skip')}
            />
          </>
        )}
        {isHabit && !failedToday && (
          <>
            <IconButton
              ref={failRef}
              icon={X}
              onClick={onFail}
              color={Colors.Error}
              aria-label={t('context-menus.task.labels.fail')}
            />
            <Tooltip
              referenceElement={failRef}
              label={t('context-menus.task.labels.fail')}
            />
          </>
        )}
        {isHabit && (
          <>
            <IconButton
              ref={logRef}
              icon={Keyboard}
              onClick={onLog}
              color={Colors.Default}
              aria-label={t('context-menus.task.labels.log-progress')}
            />
            <Tooltip
              referenceElement={logRef}
              label={t('context-menus.task.labels.log-progress')}
            />
          </>
        )}
      </QuickActionRow>
    </PopupMenuList>
  );
};
